import { KeyboardEvent, useCallback, useEffect, useState, useRef } from 'react';
import { IconButton, Box } from '@mui/material';
import TextField from 'components/TextField';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { formatMetricValue } from 'helpers/scorecards/formatMetricValue';
import { MetricFormat } from 'constants/scorecards';
import { InfoIcon } from 'components/Icons';
import Tooltip from 'components/Tooltip';
import { EditModeWrapper, ButtonsContainer } from './InlineEdit.styled';

interface InlineEditProps {
  value: string | number;
  onChange: (value: string | number) => void;
  validate?: (value: string | number) => boolean | string;
  format?: MetricFormat;
  disabled?: boolean;
  required?: boolean;
  numberValidation?: boolean;
  isEditable?: boolean;
  tooltip?: React.ReactNode;
  withActionButtons?: boolean;
  allowZero?: boolean;
}

export default function InlineEdit({
  value,
  onChange,
  validate: customValidate,
  format,
  disabled = false,
  required = false,
  numberValidation = false,
  isEditable = true,
  tooltip,
  withActionButtons = false,
  allowZero = false,
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleConfirm = useCallback(() => {
    if (required && (!editValue || editValue.toString().trim() === '')) {
      setError('This field is required');
      return;
    }

    if (numberValidation) {
      const numValue = Number(editValue);
      const numberRegex = /^[0-9.]+$/;

      if (!numberRegex.test(String(editValue))) {
        setError('Only numbers and dots are allowed');
        return;
      }

      if (Number.isNaN(numValue)) {
        setError('Please enter a valid number');
        return;
      }

      const minValue = allowZero ? 0 : 0.000001;
      const minValueFormatted = allowZero ? '0' : '0.000001';
      if (numValue < minValue || numValue > 100000000) {
        setError(`Value must be between ${minValueFormatted} and 100,000,000`);
        return;
      }
    }

    if (customValidate) {
      const validationResult = customValidate(editValue);
      if (validationResult !== true) {
        setError(
          typeof validationResult === 'string'
            ? validationResult
            : 'Invalid value',
        );
        return;
      }
    }

    onChange(editValue);
    setIsEditing(false);
    setError(null);
  }, [
    editValue,
    onChange,
    customValidate,
    required,
    numberValidation,
    allowZero,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        handleConfirm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleConfirm]);

  const handleViewClick = () => {
    if (!disabled && isEditable) {
      setIsEditing(true);
    }
  };

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
    setError(null);
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <Box onClick={handleViewClick}>
        <TextField
          value={formatMetricValue(value, format)}
          fullWidth
          InputProps={{
            endAdornment: tooltip && (
              <Tooltip className="tooltip" title={tooltip}>
                <InfoIcon />
              </Tooltip>
            ),
          }}
        />
      </Box>
    );
  }

  return (
    <EditModeWrapper ref={wrapperRef}>
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        error={Boolean(error)}
        helperText={error}
        autoFocus
        selectOnFocus
        fullWidth
      />
      {withActionButtons && (
        <ButtonsContainer>
          <IconButton size="small" onClick={handleCancel} color="default">
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleConfirm} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
        </ButtonsContainer>
      )}
    </EditModeWrapper>
  );
}
